import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Paper as MuiPaper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Tooltip,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Helmet } from "react-helmet-async";
import styled from "@emotion/styled";
import {
  Download,
  Search,
  Info,
  CheckCircleOutline,
} from "@mui/icons-material";

import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {
  fetchAllSubscriptionInvoices,
  markSubscriptionInvoicePaid,
} from "../../redux/slices/subscriptionInvoice";
import PDFViewerModal from "../PDFViewer";
import { baseUrl } from "../../config";

const Paper = styled(MuiPaper)(spacing);

const statusLabel = (status: string) => {
  switch (status) {
    case "open":
      return "Avoin";
    case "paid":
      return "Maksettu";
    case "void":
      return "Hävitetty";
    case "uncollectible":
      return "Perimätön";
    case "generating":
      return "Luodaan";
    default:
      return status;
  }
};

const statusColor = (
  status: string
): "warning" | "success" | "error" | "default" | "primary" => {
  switch (status) {
    case "open":
      return "warning";
    case "paid":
      return "success";
    case "void":
    case "uncollectible":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (val: string | null | undefined) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fi-FI");
};

const formatAmount = (cents: number | null | undefined) => {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface DetailModalProps {
  invoice: any;
  onClose: () => void;
}

const DetailModal = ({ invoice, onClose }: DetailModalProps) => (
  <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Laskun tiedot</DialogTitle>
    <DialogContent dividers>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Laskunumero
          </Typography>
          <Typography>{invoice.number ?? "—"}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Tiekunta
          </Typography>
          <Typography>{invoice.association?.name ?? "—"}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Tila
          </Typography>
          <Box mt={0.5}>
            <Chip
              label={statusLabel(invoice.status)}
              color={statusColor(invoice.status)}
              size="small"
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Valuutta
          </Typography>
          <Typography>{invoice.currency?.toUpperCase() ?? "—"}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Summa (€)
          </Typography>
          <Typography>{formatAmount(invoice.amountCents)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Verot (€)
          </Typography>
          <Typography>{formatAmount(invoice.taxCents)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Laskupäivä
          </Typography>
          <Typography>{formatDate(invoice.issuedAt)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Eräpäivä
          </Typography>
          <Typography>{formatDate(invoice.dueAt)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Kausi alku
          </Typography>
          <Typography>{formatDate(invoice.periodStart)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Kausi loppu
          </Typography>
          <Typography>{formatDate(invoice.periodEnd)}</Typography>
        </Grid>
        {invoice.externalId && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Ulkoinen ID
            </Typography>
            <Typography>{invoice.externalId}</Typography>
          </Grid>
        )}
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Sulje</Button>
    </DialogActions>
  </Dialog>
);

const SuperadminSubscriptions = () => {
  const dispatch = useAppDispatch();
  const { allInvoices, allInvoicesStatus } = useAppSelector(
    (state) => state.subscriptionInvoice
  );

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState<any | null>(null);

  useEffect(() => {
    dispatch(fetchAllSubscriptionInvoices());
  }, [dispatch]);

  const handlePreview = (invoice: any) => {
    const filename = invoice.pdf?.filename;
    if (!filename) return;
    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(true);
  };

  const handleDownload = async (invoice: any) => {
    const filename = invoice.pdf?.filename;
    if (!filename) return;
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleMarkPaid = async (invoice: any) => {
    await dispatch(
      markSubscriptionInvoicePaid(invoice.id, invoice.association?.id)
    );
    dispatch(fetchAllSubscriptionInvoices());
  };

  const isLoading = allInvoicesStatus === "loading";

  return (
    <>
      <Helmet title="Tilaukset" />
      <Typography variant="h3" gutterBottom>
        Tilauslaskut
      </Typography>
      <Divider sx={{ mb: 4 }} />

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Laskunumero</TableCell>
                <TableCell>Tiekunta</TableCell>
                <TableCell>Kausi</TableCell>
                <TableCell>Summa (€)</TableCell>
                <TableCell>Tila</TableCell>
                <TableCell>Laskupäivä</TableCell>
                <TableCell>Eräpäivä</TableCell>
                <TableCell>Toiminnot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : allInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Ei laskuja
                  </TableCell>
                </TableRow>
              ) : (
                allInvoices.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.number ?? "—"}</TableCell>
                    <TableCell>{row.association?.name ?? "—"}</TableCell>
                    <TableCell>
                      {row.periodStart || row.periodEnd
                        ? `${formatDate(row.periodStart)} – ${formatDate(
                            row.periodEnd
                          )}`
                        : "—"}
                    </TableCell>
                    <TableCell>{formatAmount(row.amountCents)}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel(row.status)}
                        color={statusColor(row.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.issuedAt)}</TableCell>
                    <TableCell>{formatDate(row.dueAt)}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          row.pdf?.filename ? "Esikatsele" : "Ei PDF-tiedostoa"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(row)}
                            disabled={!row.pdf?.filename}
                          >
                            <Search fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={row.pdf?.filename ? "Lataa" : "Ei PDF-tiedostoa"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(row)}
                            disabled={!row.pdf?.filename}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Näytä tiedot">
                        <IconButton
                          size="small"
                          onClick={() => setDetailInvoice(row)}
                        >
                          <Info fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          row.status === "paid"
                            ? "Jo maksettu"
                            : "Merkitse maksetuksi"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleMarkPaid(row)}
                            disabled={row.status === "paid"}
                            color="success"
                          >
                            <CheckCircleOutline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      {detailInvoice && (
        <DetailModal
          invoice={detailInvoice}
          onClose={() => setDetailInvoice(null)}
        />
      )}
    </>
  );
};

export default SuperadminSubscriptions;
